import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Button } from '../../components/ui/button';
import { Loader2, Save, FileDown, Copy, FolderInput, CheckCircle2, ShieldAlert, Info, ChevronDown, ChevronUp } from 'lucide-react';
import { authedUrl } from '../../lib/authSession';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CAT_STYLE = {
  Low: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  Medium: 'bg-amber-100 text-amber-800 border-amber-200',
  High: 'bg-red-100 text-red-800 border-red-200',
};

const scoreBorrower = (b, defs) => {
  const answers = b.answers || {};
  let inherent = 0;
  defs.items.forEach((it) => {
    if (it.type === 'bool') { if (answers[it.key]) inherent += it.points; }
    else { const o = (it.options || []).find((x) => x.value === answers[it.key]); if (o) inherent += o.points; }
  });
  let deductions = 0;
  if (answers.occupation_high_risk) {
    defs.mitigations.forEach((m) => { if ((b.mitigations || {})[m.key]) deductions = Math.min(defs.mitigation_cap, deductions + m.points); });
  }
  const residual = Math.max(0, inherent - deductions);
  const category = residual >= defs.thresholds.high ? 'High' : residual >= defs.thresholds.medium ? 'Medium' : 'Low';
  return { inherent, deductions, residual, category };
};

const Badge = ({ cat, testid }) => (
  <span className={`inline-block text-xs font-bold px-2 py-0.5 rounded-full border ${CAT_STYLE[cat] || 'bg-gray-100 text-gray-600'}`} data-testid={testid}>{cat}</span>
);

/** Borrower AML/TF Risk Assessment tab — scorecard + FINTRAC KYC, borrowers side by side. */
export const AmlRiskAssessmentTab = ({ fileId, agent, clientName, clientFolderId }) => {
  const [defs, setDefs] = useState(null);
  const [a, setA] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [copied, setCopied] = useState(false);
  const [folders, setFolders] = useState([]);
  const [folderId, setFolderId] = useState('');
  const [filing, setFiling] = useState(false);
  const [showSchedules, setShowSchedules] = useState(false);
  const dirty = useRef(false);
  const timer = useRef(null);
  const aRef = useRef(null);
  aRef.current = a;
  const qs = `agent_id=${encodeURIComponent(agent?.id || '')}&agent_name=${encodeURIComponent(agent?.name || 'Agent')}`;

  useEffect(() => {
    (async () => {
      try {
        const [d, r, f] = await Promise.all([
          fetch(`${API_URL}/api/mortgage/aml/definitions`),
          fetch(`${API_URL}/api/mortgage/files/${fileId}/aml?${qs}`),
          agent?.id && agent.id !== 'admin' ? fetch(`${API_URL}/api/client-folders/${agent.id}`) : Promise.resolve(null),
        ]);
        if (d.ok) setDefs(await d.json());
        if (r.ok) setA(await r.json());
        if (f && f.ok) {
          const list = await f.json();
          setFolders(list);
          const match = list.find((x) => x.id === clientFolderId) || list.find((x) => (x.client_name || '').toLowerCase() === (clientName || '').toLowerCase());
          if (match) setFolderId(match.id);
        }
      } catch (e) { console.error('AML load failed', e); } finally { setLoading(false); }
    })();
  }, [fileId]); // eslint-disable-line react-hooks/exhaustive-deps

  const persist = useCallback(async (payload) => {
    setSaving(true);
    try {
      const r = await fetch(`${API_URL}/api/mortgage/files/${fileId}/aml?${qs}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload),
      });
      if (r.ok) {
        const d = await r.json();
        setA((cur) => ({
          ...cur, velocity_summary: d.velocity_summary, assessed_at: d.assessed_at, filed_to_folder: d.filed_to_folder,
          high_alert_sent_at: d.high_alert_sent_at, high_alert_to: d.high_alert_to,
          borrowers: cur.borrowers.map((b, i) => {
            const sb = d.borrowers?.[i];
            if (!sb) return b;
            const answers = { ...b.answers };
            ['occupation_high_risk', 'years_in_canada'].forEach((k) => { if (!(b.manual || {})[k] && sb.auto?.[k]) answers[k] = sb.auto[k].value; });
            const kyc = { ...b.kyc };
            ['occupation', 'employer'].forEach((k) => { if ((!kyc[k] || kyc[k] === (b.auto_kyc || {})[k]) && sb.kyc?.[k]) kyc[k] = sb.kyc[k]; });
            return { ...b, auto: sb.auto || {}, auto_kyc: sb.auto_kyc || {}, answers, kyc };
          }),
        }));
        setSavedAt(Date.now()); setTimeout(() => setSavedAt(null), 2000); dirty.current = false;
      }
      else alert('Save failed: ' + (await r.text()));
    } catch (e) { alert('Save failed: ' + e.message); } finally { setSaving(false); }
  }, [fileId, qs]);

  const update = (fn) => {
    setA((cur) => { const next = fn(cur); dirty.current = true; return next; });
    clearTimeout(timer.current);
    timer.current = setTimeout(() => { if (dirty.current && aRef.current) persist(aRef.current); }, 1200);
  };
  const setTop = (k, v) => update((cur) => ({ ...cur, [k]: v }));
  const AUTO_KEYS = ['occupation_high_risk', 'years_in_canada'];
  const setB = (i, section, k, v) => update((cur) => {
    const bs = cur.borrowers.map((b, j) => {
      if (j !== i) return b;
      const next = { ...b, [section]: { ...(b[section] || {}), [k]: v } };
      if (section === 'answers' && AUTO_KEYS.includes(k)) next.manual = { ...(b.manual || {}), [k]: true };
      return next;
    });
    return { ...cur, borrowers: bs };
  });
  const resetAuto = (i, k) => update((cur) => ({
    ...cur,
    borrowers: cur.borrowers.map((b, j) => (j === i ? { ...b, manual: { ...(b.manual || {}), [k]: false }, answers: { ...b.answers, [k]: b.auto?.[k]?.value ?? b.answers[k] } } : b)),
  }));
  const renderAutoNote = (b, i, k) => {
    const au = b.auto?.[k];
    if (!au) return null;
    const manual = !!(b.manual || {})[k];
    return (
      <p className={`text-[10px] mt-1 leading-tight ${manual ? 'text-gray-400' : 'text-[#0077B6]'}`} data-testid={`aml-auto-${k}-${i}`}>
        {manual ? 'Manually set' : 'Auto'}: {au.source ? `“${au.source}” — ` : ''}{au.reason}
        {manual && <button type="button" onClick={() => resetAuto(i, k)} className="ml-1 underline" data-testid={`aml-auto-reset-${k}-${i}`}>use auto</button>}
      </p>
    );
  };
  const copyToOthers = () => update((cur) => ({
    ...cur,
    borrowers: cur.borrowers.map((b, j) => (j === 0 ? b : { ...b, answers: { ...cur.borrowers[0].answers }, mitigations: { ...cur.borrowers[0].mitigations } })),
  }));

  const scores = useMemo(() => (defs && a ? a.borrowers.map((b) => scoreBorrower(b, defs)) : []), [defs, a]);
  const overall = scores.reduce((m, s) => Math.max(m, s.residual), 0);
  const overallCat = defs ? (overall >= defs.thresholds.high ? 'High' : overall >= defs.thresholds.medium ? 'Medium' : 'Low') : 'Low';

  const copyVelocity = async () => {
    try { await navigator.clipboard.writeText(a.velocity_summary || ''); setCopied(true); setTimeout(() => setCopied(false), 2000); }
    catch (e) { alert('Copy failed — select the text and copy manually.'); }
  };

  const fileToFolder = async () => {
    if (!folderId) return;
    setFiling(true);
    try {
      if (dirty.current) await persist(a);
      const r = await fetch(`${API_URL}/api/mortgage/files/${fileId}/aml/save-to-folder`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ folder_id: folderId }),
      });
      const d = await r.json();
      if (r.ok) { setA((cur) => ({ ...cur, filed_to_folder: { folder_id: folderId, client_name: d.folder.client_name, file_id: d.file.id, at: d.file.created_at } })); alert(`Saved "${d.file.filename}" into ${d.folder.client_name}'s folder.`); }
      else alert('Could not file the PDF: ' + (d.detail || r.status));
    } catch (e) { alert('Could not file the PDF: ' + e.message); } finally { setFiling(false); }
  };

  if (loading || !defs || !a) return <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-[#0A2A43]" /></div>;

  const n = a.borrowers.length;
  const grid = { gridTemplateColumns: `minmax(220px, 1.6fr) repeat(${n}, minmax(150px, 1fr))` };
  const needsEdd = overallCat !== 'Low';
  const groups = [...new Set(defs.items.map((i) => i.group))];

  return (
    <div className="space-y-5" data-testid="aml-tab">
      <div className="bg-gradient-to-r from-[#0A2A43] to-[#0d3a5c] text-white rounded-lg p-4 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="font-bold flex items-center gap-2"><ShieldAlert className="h-5 w-5" /> Borrower AML / TF Risk Assessment</h3>
          <p className="text-xs opacity-90 mt-1">Complete with the client. Inherent-risk points less mitigations (max −{defs.mitigation_cap}) give the residual score: Low &lt; {defs.thresholds.medium}, Medium {defs.thresholds.medium}–{defs.thresholds.high - 1}, High ≥ {defs.thresholds.high}. Answers autosave.</p>
        </div>
        <div className="flex items-center gap-2">
          {saving && <span className="text-xs flex items-center gap-1"><Loader2 className="h-3 w-3 animate-spin" /> Saving…</span>}
          {!saving && savedAt && <span className="text-xs flex items-center gap-1 text-emerald-300" data-testid="aml-saved"><CheckCircle2 className="h-4 w-4" /> Saved</span>}
          <Button size="sm" variant="secondary" onClick={() => persist(a)} disabled={saving} data-testid="aml-save-btn"><Save className="h-4 w-4 mr-1" /> Save</Button>
        </div>
      </div>

      {/* Summary */}
      <div className="grid gap-px bg-gray-200 border rounded-lg overflow-hidden text-sm" style={grid} data-testid="aml-summary">
        <div className="bg-gray-50 p-2 font-semibold text-gray-700">Summary</div>
        {a.borrowers.map((b, i) => <div key={i} className="bg-gray-50 p-2 font-semibold text-[#0A2A43] truncate" data-testid={`aml-borrower-head-${i}`}>Borrower {i + 1}: {b.name || '—'}</div>)}
        {[['Total Inherent Risk', 'inherent'], ['Mitigation Deductions', 'deductions'], ['Total Residual Risk', 'residual']].map(([lbl, k]) => (
          <React.Fragment key={k}>
            <div className="bg-white p-2 text-gray-700">{lbl}</div>
            {scores.map((s, i) => <div key={i} className="bg-white p-2 font-mono font-semibold" data-testid={`aml-${k}-${i}`}>{k === 'deductions' && s.deductions ? `−${s.deductions}` : s[k]}</div>)}
          </React.Fragment>
        ))}
        <div className="bg-white p-2 text-gray-700">Risk Category</div>
        {scores.map((s, i) => <div key={i} className="bg-white p-2"><Badge cat={s.category} testid={`aml-category-${i}`} /></div>)}
      </div>
      <div className="flex flex-wrap items-center gap-3 text-sm">
        <span className="text-gray-600">Overall file rating:</span><Badge cat={overallCat} testid="aml-overall-category" />
        <span className="text-gray-400 text-xs">(highest borrower residual: {overall})</span>
        {overallCat === 'High' && (
          <span className="text-xs text-red-700 font-semibold" data-testid="aml-high-alert-note">
            {a.high_alert_sent_at ? `Compliance officer alerted ${new Date(a.high_alert_sent_at).toLocaleString()}` : 'Compliance officer is alerted when this High rating is saved'}
          </span>
        )}
        <div className="ml-auto flex flex-wrap items-center gap-2">
          <label className="text-xs text-gray-600">Date <input type="date" value={a.assessment_date || ''} onChange={(e) => setTop('assessment_date', e.target.value)} className="ml-1 border rounded px-2 py-1 text-xs" data-testid="aml-date" /></label>
          <label className="text-xs text-gray-600">Internal file # <input value={a.internal_file_no || ''} onChange={(e) => setTop('internal_file_no', e.target.value)} className="ml-1 border rounded px-2 py-1 text-xs w-28" data-testid="aml-file-no" /></label>
          <label className="text-xs text-gray-600">Assessed by <input value={a.assessed_by || ''} onChange={(e) => setTop('assessed_by', e.target.value)} className="ml-1 border rounded px-2 py-1 text-xs w-36" data-testid="aml-assessed-by" /></label>
        </div>
      </div>

      {/* Inherent risk items */}
      <div className="border rounded-lg overflow-hidden">
        <div className="flex items-center justify-between bg-[#0A2A43] text-white px-3 py-2">
          <h4 className="text-sm font-bold">Customer Inherent Risk Assessment</h4>
          {n > 1 && <button type="button" onClick={copyToOthers} className="text-xs underline opacity-90 hover:opacity-100" data-testid="aml-copy-borrower1">Copy Borrower 1 answers to all</button>}
        </div>
        <div className="grid gap-px bg-gray-200 text-sm" style={grid}>
          {groups.map((g) => (
            <React.Fragment key={g}>
              <div className="bg-[#EEF2F7] px-3 py-1.5 text-xs font-bold text-[#0A2A43] uppercase tracking-wide" style={{ gridColumn: `1 / span ${n + 1}` }}>{g}</div>
              {defs.items.filter((it) => it.group === g).map((it) => (
                <React.Fragment key={it.key}>
                  <div className="bg-white px-3 py-2 text-gray-800 flex items-start justify-between gap-2">
                    <span>{it.label}</span>
                    {it.type === 'bool' && <span className="text-xs font-mono text-red-700 whitespace-nowrap">+{it.points}</span>}
                  </div>
                  {a.borrowers.map((b, i) => (
                    <div key={i} className="bg-white px-3 py-2">
                      {it.type === 'bool' ? (
                        <>
                          <label className="inline-flex items-center gap-2 cursor-pointer">
                            <input type="checkbox" checked={!!(b.answers || {})[it.key]} onChange={(e) => setB(i, 'answers', it.key, e.target.checked)} className="h-4 w-4 accent-[#0A2A43]" data-testid={`aml-${it.key}-${i}`} />
                            <span className="text-xs text-gray-600">{(b.answers || {})[it.key] ? 'Yes' : 'No'}</span>
                          </label>
                          {renderAutoNote(b, i, it.key)}
                        </>
                      ) : (
                        <>
                          <select value={(b.answers || {})[it.key] || ''} onChange={(e) => setB(i, 'answers', it.key, e.target.value)} className="w-full border rounded px-2 py-1 text-xs bg-white" data-testid={`aml-${it.key}-${i}`}>
                            <option value="">Select…</option>
                            {it.options.map((o) => <option key={o.value} value={o.value}>{o.label}{o.points ? ` (+${o.points})` : ''}</option>)}
                          </select>
                          {renderAutoNote(b, i, it.key)}
                        </>
                      )}
                    </div>
                  ))}
                </React.Fragment>
              ))}
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* Mitigations */}
      <div className="border rounded-lg overflow-hidden">
        <div className="bg-[#0A2A43] text-white px-3 py-2"><h4 className="text-sm font-bold">Customer Inherent Risk Mitigating Factors</h4><p className="text-[11px] opacity-80">Enabled only when "High-Risk Occupation" is selected for that borrower. Only {defs.mitigation_cap} points can be applied.</p></div>
        <div className="grid gap-px bg-gray-200 text-sm" style={grid}>
          {defs.mitigations.map((m) => (
            <React.Fragment key={m.key}>
              <div className="bg-white px-3 py-2 text-gray-800 flex justify-between gap-2"><span>{m.label}</span><span className="text-xs font-mono text-emerald-700">−{m.points}</span></div>
              {a.borrowers.map((b, i) => {
                const enabled = !!(b.answers || {}).occupation_high_risk;
                return (
                  <div key={i} className={`px-3 py-2 ${enabled ? 'bg-white' : 'bg-gray-50'}`}>
                    <input type="checkbox" disabled={!enabled} checked={!!(b.mitigations || {})[m.key]} onChange={(e) => setB(i, 'mitigations', m.key, e.target.checked)} className="h-4 w-4 accent-[#0A2A43] disabled:opacity-40" data-testid={`aml-mit-${m.key}-${i}`} />
                  </div>
                );
              })}
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* KYC record */}
      <div className="border rounded-lg overflow-hidden">
        <div className="bg-[#0A2A43] text-white px-3 py-2"><h4 className="text-sm font-bold">Know-Your-Client Record (FINTRAC)</h4><p className="text-[11px] opacity-80">Required record-keeping details — identity, PEP/HIO, third-party, source of funds, purpose.</p></div>
        <div className="grid gap-px bg-gray-200 text-sm" style={grid}>
          {defs.kyc_fields.map((f) => (
            <React.Fragment key={f.key}>
              <div className="bg-white px-3 py-2 text-gray-800">{f.label}</div>
              {a.borrowers.map((b, i) => (
                <div key={i} className="bg-white px-2 py-1.5">
                  <input type={f.key === 'id_expiry' ? 'date' : f.key === 'arrived_in_canada' ? 'month' : 'text'} value={(b.kyc || {})[f.key] || ''} onChange={(e) => setB(i, 'kyc', f.key, e.target.value)} className="w-full border rounded px-2 py-1 text-xs" data-testid={`aml-kyc-${f.key}-${i}`} />
                  {f.key === 'occupation' && <p className="text-[10px] text-gray-400 mt-0.5">Pre-filled from Position / Job Title on the Income tab; drives the Schedule A answer above.</p>}
                  {f.key === 'arrived_in_canada' && <p className="text-[10px] text-gray-400 mt-0.5">Drives "Time in Canada" above.</p>}
                </div>
              ))}
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* EDD */}
      <div className={`border rounded-lg p-4 space-y-3 ${needsEdd ? 'border-amber-300 bg-amber-50/40' : 'bg-gray-50'}`} data-testid="aml-edd">
        <h4 className="text-sm font-bold text-[#0A2A43]">Enhanced Due Diligence {needsEdd ? <span className="text-amber-700 text-xs font-semibold">— required for Medium / High</span> : <span className="text-gray-400 text-xs font-normal">— only needed when the rating is Medium or High</span>}</h4>
        <div>
          <label className="block text-xs font-semibold text-gray-700 mb-1">What Enhanced Due Diligence steps did you take?</label>
          <textarea rows={3} disabled={!needsEdd} value={a.edd_notes || ''} onChange={(e) => setTop('edd_notes', e.target.value)} className="w-full border rounded px-3 py-2 text-sm disabled:bg-gray-100 disabled:text-gray-400" data-testid="aml-edd-notes" />
        </div>
        <div>
          <label className="block text-xs font-semibold text-gray-700 mb-1">Given the Medium/High rating, why are you comfortable proceeding with this file?</label>
          <textarea rows={3} disabled={!needsEdd} value={a.comfort_notes || ''} onChange={(e) => setTop('comfort_notes', e.target.value)} className="w-full border rounded px-3 py-2 text-sm disabled:bg-gray-100 disabled:text-gray-400" data-testid="aml-comfort-notes" />
        </div>
      </div>

      {/* Outputs */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="border rounded-lg p-4 space-y-2" data-testid="aml-velocity">
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-bold text-[#0A2A43]">Copy and Paste into Velocity AML Notes</h4>
            <Button size="sm" variant="outline" onClick={copyVelocity} data-testid="aml-copy-velocity-btn">{copied ? <CheckCircle2 className="h-4 w-4 mr-1 text-emerald-600" /> : <Copy className="h-4 w-4 mr-1" />}{copied ? 'Copied' : 'Copy'}</Button>
          </div>
          <pre className="text-[11px] leading-snug bg-gray-50 border rounded p-3 whitespace-pre-wrap max-h-72 overflow-auto font-mono" data-testid="aml-velocity-text">{a.velocity_summary || 'Save to generate the summary.'}</pre>
          {needsEdd && (!a.edd_notes?.trim() || !a.comfort_notes?.trim()) && <p className="text-xs text-amber-700 flex items-center gap-1"><Info className="h-3.5 w-3.5" /> Fill in the EDD notes before pasting — they are required for Medium/High files.</p>}
        </div>
        <div className="border rounded-lg p-4 space-y-3">
          <h4 className="text-sm font-bold text-[#0A2A43]">Downloadable PDF</h4>
          <p className="text-xs text-gray-600">One document for the file, borrowers side by side, with the KYC record and Schedules A/B.</p>
          <Button asChild variant="outline" size="sm"><a href={authedUrl(`${API_URL}/api/mortgage/files/${fileId}/aml/pdf`)} target="_blank" rel="noopener noreferrer" data-testid="aml-download-pdf"><FileDown className="h-4 w-4 mr-1" /> Download AML Risk Assessment PDF</a></Button>
          {folders.length > 0 && (
            <div className="pt-2 border-t space-y-2">
              <label className="block text-xs font-semibold text-gray-700">Save a copy into My Clients' Folder</label>
              <div className="flex gap-2">
                <select value={folderId} onChange={(e) => setFolderId(e.target.value)} className="flex-1 border rounded px-2 py-1.5 text-sm" data-testid="aml-folder-select">
                  <option value="">Select client folder…</option>
                  {folders.map((f) => <option key={f.id} value={f.id}>{f.client_name}</option>)}
                </select>
                <Button size="sm" onClick={fileToFolder} disabled={!folderId || filing} className="bg-[#0A2A43] text-white" data-testid="aml-file-to-folder-btn">{filing ? <Loader2 className="h-4 w-4 animate-spin" /> : <FolderInput className="h-4 w-4 mr-1" />} File it</Button>
              </div>
              {a.filed_to_folder && <p className="text-xs text-emerald-700" data-testid="aml-filed-note">Filed into {a.filed_to_folder.client_name}'s folder on {new Date(a.filed_to_folder.at).toLocaleString()}.</p>}
            </div>
          )}
        </div>
      </div>

      <button type="button" onClick={() => setShowSchedules((s) => !s)} className="text-xs text-[#0A2A43] underline flex items-center gap-1" data-testid="aml-toggle-schedules">{showSchedules ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />} Schedule A (High-Risk Occupations) & Schedule B (High-Risk Jurisdictions)</button>
      {showSchedules && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs text-gray-700" data-testid="aml-schedules">
          <div><p className="font-bold mb-1">Schedule A — High-Risk Occupations</p><ul className="list-disc pl-4 space-y-0.5">{defs.schedule_a.map((s) => <li key={s}>{s}</li>)}</ul></div>
          <div><p className="font-bold mb-1">Schedule B — High-Risk Jurisdictions</p><ul className="list-disc pl-4 space-y-0.5">{defs.schedule_b.map((s) => <li key={s}>{s}</li>)}</ul></div>
        </div>
      )}
    </div>
  );
};

export default AmlRiskAssessmentTab;
